import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
  MsalService
} from '@azure/msal-angular';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AlertDialogComponent } from './components/alert-dialog/alert-dialog.component';
import { AlertsComponent } from './components/alerts/alerts.component';
import { ConfirmLogoutDialogComponent } from './components/confirm-logout-dialog/confirm-logout-dialog.component';
import { DriverWelcomeModalComponent } from './components/driver-welcome-modal/driver-welcome-modal.component';
import { MapComponent } from './components/map/map.component';
import { NavBarComponent } from './components/nav-bar/nav-bar.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { PreferredDriversDialogComponent } from './components/preferred-drivers-dialog/preferred-drivers-dialog.component';
import { SplashComponent } from './components/splash/splash.component';
import { ToastsContainer } from './components/toast-global/toast-container.component';
import { NgbdToastGlobal } from './components/toast-global/toast-global.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { VerticalMenuComponent } from './components/vertical-menu/vertical-menu.component';
import { CvvCommonModule } from './modules/cvv-common/cvv-common.module';
import { DispatchModule } from './modules/dispatch/dispatch.module';
import { ManageVansModule } from './modules/manage-vans/manage-vans.module';
import { MyTasksModule } from './modules/my-tasks/my-tasks.module';
import { NewTaskModule } from './modules/new-task/new-task.module';
import { OutboundModule } from './modules/outbound/outbound.module';
import { ProfileModule } from './modules/profile/profile.module';
import { SearchModule } from './modules/search/search.module';
import { SettingsModule } from './modules/settings/settings.module';
import { TerminalRunsModule } from './modules/terminal-runs/terminal-runs.module';
import { UnassignedTasksModule } from './modules/unassigned-tasks/unassigned-tasks.module';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AlertService } from './services/alert.service';
import { AssetService } from './services/asset.service';
import { ConfigurationService } from './services/configuration.service';
import { CoordinatesService } from './services/coordinates.service';
import { DispatchService } from './services/dispatch.service';
import { EventsDeltaService } from './services/events-delta.service';
import { EventsService } from './services/events.service';
import { GeoLocationService } from './services/geo-location.service';
import { InMemoryDataService } from './services/in-memory-data.service';
import { IosService } from './services/ios.service';
import { ManageVansService } from './services/manage-vans.service';
import { MapIconService } from './services/map-icon.service';
import { MapLayerService } from './services/map-layer.service';
import { MapMarkerService } from './services/map-marker.service';
import { MapPopUpService } from './services/map-popup.service';
import { MapShapeService } from './services/map-shape.service';
import { MsalConfigService } from './services/msal-config.service';
import { MyTasksService } from './services/my-tasks.service';
import { NavService } from './services/nav.service';
import { OutboundService } from './services/outbound.service';
import { ProcessDataService } from './services/process-data.service';
import { ProfileService } from './services/profile.service';
import { RolesService } from './services/roles.service';
import { SearchService } from './services/search.service';
import { SettingsService } from './services/settings.service';
import { SideNavService } from './services/side-nav.service';
import { TerminalRunsService } from './services/terminal-runs.service';
import { ToastService } from './services/toast.service';
import { UnassignedTasksService } from './services/unassigned-tasks.service';
import { CvvMsalInterceptor } from './interceptors/cvv-msal.interceptor';
import { AuthConfigService } from './services/auth-config.service';
import { EnvConfigService } from './services/env-config.service';

const appInitializerFn = (appConfig: ConfigurationService) => {
  return () => {
    return appConfig.loadConfig();
  };
};

const useImMemoryWebApi = !environment.production && environment.inMemoryWebApiEnabled;

@NgModule({
  declarations: [
    AppComponent,
    SplashComponent,
    MapComponent,
    VerticalMenuComponent,
    AlertsComponent,
    NavBarComponent,
    NgbdToastGlobal,
    ToastsContainer,
    UnauthorizedComponent,
    AlertDialogComponent,
    PageNotFoundComponent,
    PreferredDriversDialogComponent,
    DriverWelcomeModalComponent,
    ConfirmLogoutDialogComponent
    // EndTaskReasonDialogComponent,
    // ConfirmPassengerNotCheckedInDialogComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    useImMemoryWebApi
      ? HttpClientInMemoryWebApiModule.forRoot(InMemoryDataService, {
          delay: 500,
          passThruUnknownUrl: true,
          put204: true
        })
      : [],
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatSidenavModule,
    MatFormFieldModule,
    MatMenuModule,
    MatCardModule,
    MatTabsModule,
    MatExpansionModule,
    MatGridListModule,
    MatDialogModule,
    ProfileModule,
    DispatchModule,
    MyTasksModule,
    UnassignedTasksModule,
    SearchModule,
    TerminalRunsModule,
    SettingsModule,
    OutboundModule,
    ManageVansModule,
    NewTaskModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    MatSlideToggleModule,
    NgbModule,
    CvvCommonModule,
    NgIdleKeepaliveModule.forRoot(),
    MsalModule,
    MatDialogModule,
    MatBadgeModule,
    MatTooltipModule,
    ScrollingModule
  ],
  providers: [
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MsalConfigService,
    AuthConfigService,
    EnvConfigService,
    { provide: HTTP_INTERCEPTORS, useClass: CvvMsalInterceptor, multi: true },
    ConfigurationService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFn,
      multi: true,
      deps: [ ConfigurationService ]
    },
    MapMarkerService,
    MapPopUpService,
    MapShapeService,
    ProfileService,
    MapIconService,
    AlertService,
    AssetService,
    DispatchService,
    EventsService,
    EventsDeltaService,
    ManageVansService,
    MyTasksService,
    OutboundService,
    TerminalRunsService,
    SettingsService,
    UnassignedTasksService,
    SearchService,
    SideNavService,
    MapLayerService,
    ToastService,
    RolesService,
    NavService,
    ProcessDataService,
    CoordinatesService,
    IosService,
    GeoLocationService,
    {
      provide: MatDialogRef,
      useValue: {}
    },
    {
      provide: MAT_DIALOG_DATA,
      useValue: {}
    },
    provideHttpClient(withInterceptorsFromDi())
  ],
  bootstrap: [ AppComponent, MsalRedirectComponent ]
})
export class AppModule {}
